/**
 * Transcription component that displays live transcription
 * as video subtitles at the bottom center of the screen
 */
import { useEffect, useState, useMemo } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent, DataPacket_Kind, RemoteParticipant } from 'livekit-client';

type TranscriptItem = {
	id: string;
	text: string;
	identity: string;
	time: number;
};

export const Transcription = () => {
	const room = useRoomContext();
	const [visible, setVisible] = useState<boolean>(false);
	const [transcripts, setTranscripts] = useState<TranscriptItem[]>([]);

	// Deduplicate, limit, and merge close segments from same speaker (<=500ms gap)
	const mergedList = useMemo(() => {
		if (transcripts.length === 0) return [];

		// Deduplicate by id
		const uniqueMap = new Map<string, TranscriptItem>();
		transcripts.forEach((t) => {
			if (!uniqueMap.has(t.id)) {
				uniqueMap.set(t.id, t);
			}
		});

		// Sort oldest -> newest
		const sorted = Array.from(uniqueMap.values()).sort((a, b) => a.time - b.time);

		// Limit to latest N for performance
		const MAX_SHOW = 20;
		const limited = sorted.slice(-MAX_SHOW);

		// Merge consecutive segments from same speaker within 500ms
		const MERGE_GAP = 500;
		const merged: TranscriptItem[] = [];
		for (const item of limited) {
			const last = merged[merged.length - 1];
			if (last && last.identity === item.identity && item.time - last.time <= MERGE_GAP) {
				last.text = `${last.text} ${item.text}`.trim();
				last.time = item.time;
			} else {
				merged.push({ ...item });
			}
		}

		return merged;
	}, [transcripts]);

	useEffect(() => {
		if (!room) return;

		const handleDataReceived = (
			payload: Uint8Array,
			participant?: RemoteParticipant,
			kind?: DataPacket_Kind,
			topic?: string
		) => {
			if (topic !== "transcription") return;

			try {
				const decoder = new TextDecoder();
				const strData = decoder.decode(payload);
				const data = JSON.parse(strData);
				
				if (data.type === "transcripts" && Array.isArray(data.transcripts)) {
					setTranscripts(data.transcripts);
					
					if (data.transcripts.length > 0) {
						setVisible(true);
					}
				}
			} catch (error) {
				console.error("Error processing received data:", error);
			}
		};

		room.on(RoomEvent.DataReceived, handleDataReceived);

		return () => {
			room.off(RoomEvent.DataReceived, handleDataReceived);
		};
	}, [room]);

	// Keep always visible when we have any items; hide only if none.
	useEffect(() => {
		if (mergedList.length === 0) {
			setVisible(false);
		} else {
			setVisible(true);
		}
	}, [mergedList]);

	if (!visible || mergedList.length === 0) {
		return null;
	}

	// Inline styles: stack bottom-right, latest at the bottom with higher opacity
	const containerStyle: React.CSSProperties = {
		position: 'fixed',
		right: '2%',
		bottom: '6%',
		zIndex: 1000,
		maxWidth: '32%',
		maxHeight: '60vh',
		textAlign: 'right',
		padding: '0',
		pointerEvents: 'auto',
		animation: 'subtitleFadeIn 0.3s ease-in',
		wordWrap: 'break-word',
		overflowWrap: 'break-word',
		overflowY: 'auto',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-end', // newest at the bottom
	};

	const textStyle: React.CSSProperties = {
		color: '#ffffff',
		fontSize: 'clamp(14px, 1.6vw, 18px)',
		fontWeight: 400,
		lineHeight: 1.4,
		margin: '6px 0',
		padding: '8px 12px',
		backgroundColor: 'rgba(0,0,0,0.55)',
		borderRadius: '6px',
		textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)',
		fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
	};

	return (
		<>
			<style>{`
				@keyframes subtitleFadeIn {
					from {
						opacity: 0;
						transform: translateX(-50%) translateY(10px);
					}
					to {
						opacity: 1;
						transform: translateX(-50%) translateY(0);
					}
				}
			`}</style>
			<div style={containerStyle}>
				{[...mergedList]
					.sort((a, b) => a.time - b.time) // oldest to newest
					.map((segment, index, arr) => {
						const positionFromEnd = arr.length - 1 - index; // 0 = newest
						const opacity = Math.max(0.25, 1 - positionFromEnd * 0.15);
						return (
							<div
								key={`${segment.id}-${segment.time}-${index}`}
								style={{ ...textStyle, opacity }}
							>
								{segment.identity ? `${segment.identity}: ` : ''}{segment.text}
							</div>
						);
					})}
			</div>
		</>
	);
};
